import { db } from '@/db/dexieClient';
import { uow } from '@/db/unitOfWork';
import type {
  DeviceImageApiResponse,
  DeviceImageRecord,
  DeviceImageReferenceDateValue,
  DeviceImageReferenceDto,
  DeviceImageUploadContext,
} from '@/domains/deviceImages/types/deviceImagesType';

// Cache name (shared with syncService)
export const DEVICES_IMAGES_CACHE_NAME = 'media-images-v1';

// Type for metadata input, Omit now includes generic fields
type ImageMetadataInput = Omit<DeviceImageRecord, 'id' | 'cacheKey'>;

const REMOTE_CACHE_KEY_PREFIX = 'remote-image-ref';
const LOCAL_CACHE_KEY_PREFIX = '/hp-media/';

const createTemporaryLocalId = (): number => -Date.now() - Math.floor(Math.random() * 1000);

const parseDateToMs = (value: DeviceImageReferenceDateValue | undefined): number | null => {
  if (!value) return null;
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const buildCompositeKey = (
  entityId: number,
  deviceImageTypeId: number,
  timestamp: number
): string => `${entityId}:${deviceImageTypeId}:${timestamp}`;

const getReferenceTimestamp = (dto: DeviceImageReferenceDto): number =>
  parseDateToMs(dto.syncedOn) ??
  parseDateToMs(dto.updatedDate) ??
  parseDateToMs(dto.createdDate) ??
  Date.now();

const getCompositeLookupKey = (dto: DeviceImageReferenceDto): string | null => {
  if (!dto.entityId || !dto.deviceImageTypeId) {
    return null;
  }

  return `composite:${buildCompositeKey(dto.entityId, dto.deviceImageTypeId, getReferenceTimestamp(dto))}`;
};

const getServerLookupKey = (dto: DeviceImageReferenceDto): string | null => {
  if (dto.id == null) {
    return null;
  }
  return `server:${dto.id}`;
};

const isLocalPendingRecord = (record: DeviceImageRecord | undefined): boolean =>
  Boolean(
    record &&
    (record.syncStatus === 'pending' ||
      record.syncStatus === 'syncing' ||
      record.syncStatus === 'failed')
  );

const mapReferenceForUpsert = (
  dto: DeviceImageReferenceDto,
  cachedIndex: Map<string, DeviceImageRecord>
): DeviceImageRecord | null => {
  const serverKey = getServerLookupKey(dto);
  const compositeKey = getCompositeLookupKey(dto);

  const matchedRecord =
    (serverKey ? cachedIndex.get(serverKey) : undefined) ??
    (compositeKey ? cachedIndex.get(compositeKey) : undefined);

  if (isLocalPendingRecord(matchedRecord)) {
    return null;
  }

  return toReferenceRecord(dto, matchedRecord);
};

const toReferenceRecord = (
  dto: DeviceImageReferenceDto,
  matchedRecord?: DeviceImageRecord
): DeviceImageRecord | null => {
  if (!dto.entityId || !dto.deviceImageTypeId) {
    return null;
  }

  const timestampFromDates =
    parseDateToMs(dto.syncedOn) ?? parseDateToMs(dto.updatedDate) ?? parseDateToMs(dto.createdDate);
  const timestamp = timestampFromDates ?? Date.now();

  const existingLocalCacheKey = matchedRecord?.cacheKey?.startsWith(LOCAL_CACHE_KEY_PREFIX)
    ? matchedRecord.cacheKey
    : null;
  const cacheKey =
    existingLocalCacheKey ||
    dto.cacheKey?.trim() ||
    `${REMOTE_CACHE_KEY_PREFIX}:${dto.id ?? 'unknown'}:${dto.entityId}:${dto.deviceImageTypeId}:${timestamp}`;

  const resolvedId = dto.id ?? matchedRecord?.id;

  return {
    id: resolvedId,
    entityId: dto.entityId,
    deviceImageTypeId: dto.deviceImageTypeId,
    timestamp,
    cacheKey,
    syncStatus: 'synced',
    retryCount: 0,
    routeId: dto.routeId ?? null,
    routeStopId: dto.routeStopId ?? null,
    shipmentGuid: dto.shipmentGuid ?? null,
  };
};

const indexCachedRecords = (records: DeviceImageRecord[]): Map<string, DeviceImageRecord> => {
  const index = new Map<string, DeviceImageRecord>();
  for (const record of records) {
    if (record.id != null && record.syncStatus === 'synced') {
      index.set(`server:${record.id}`, record);
    }
    index.set(
      `composite:${buildCompositeKey(record.entityId, record.deviceImageTypeId, record.timestamp)}`,
      record
    );
  }
  return index;
};

// --- Public API: Get Operations ---
export const getDeviceImagesByStopId = async (
  routeStopId: number
): Promise<DeviceImageRecord[]> => {
  return db.deviceImages.where('routeStopId').equals(routeStopId).toArray();
};

export const deleteDeviceImageLocalById = async (imageId: number): Promise<void> => {
  const imageRecord = await db.deviceImages.get(imageId);
  if (!imageRecord) {
    return;
  }

  await deleteDeviceImageLocalRecord(imageRecord);
};

export const deleteDeviceImageLocalRecord = async (
  imageRecord: DeviceImageRecord
): Promise<void> => {
  const imageId = imageRecord.id;
  const cacheKey = imageRecord.cacheKey;

  await uow.runRW([db.deviceImages, db.syncQueue], async () => {
    if (imageId != null) {
      await db.deviceImages.delete(imageId);
      const relatedJobs = await db.syncQueue
        .toArray()
        .then((jobs) =>
          jobs.filter(
            (job) =>
              job.type === 'IMAGE_CREATE' &&
              (job.newRecord as { imageId?: number })?.imageId === imageId
          )
        );

      if (relatedJobs.length > 0) {
        await db.syncQueue.bulkDelete(relatedJobs.map((job) => job.id));
      }
    }
  });

  if (cacheKey) {
    const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME);
    await cache.delete(cacheKey);
  }
};

// --- Public API: Save Operations ---
/**
 * Saves media data to offline storage.
 * 1. Saves the image blob to Cache Storage API.
 * 2. Saves metadata (entity ID, type, location) to Dexie.
 */
export const saveMediaToOfflineStorage = async (
  blob: Blob,
  metadata: ImageMetadataInput
): Promise<DeviceImageRecord> => {
  try {
    // 1. Generate generic cache key (root-relative so it resolves identically from any page)
    const cacheKey = `${LOCAL_CACHE_KEY_PREFIX}media_${metadata.deviceImageTypeId}_${metadata.entityId}_${metadata.timestamp}.jpg`;
    const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME); // 2. Save blob to cache

    await cache.put(
      cacheKey,
      new Response(blob, {
        headers: { 'Content-Type': 'image/jpeg' },
      })
    ); // 3. Create complete record for Dexie

    const mediaRecord: DeviceImageRecord = {
      id: createTemporaryLocalId(),
      ...metadata,
      cacheKey: cacheKey,
    }; // 4. Save metadata to Dexie (in the new table)

    await uow.runRW([db.deviceImages], async () => {
      await db.deviceImages.put(mediaRecord);
    });

    return mediaRecord;
  } catch (error) {
    console.error('Failed to save media to offline storage:', error);
    throw new Error('Failed to save media offline.');
  }
};

/**
 * Saves successful API upload to cache for offline access.
 * This is used when an image is successfully uploaded to the API
 * and we want to store it locally for reference.
 */
export const saveSuccessfulUploadToCache = async (
  apiResponse: DeviceImageApiResponse,
  metadata: DeviceImageUploadContext,
  blob?: Blob
): Promise<DeviceImageRecord> => {
  try {
    // Root-relative prefix ensures key resolves identically from any page (avoids relative-URL mismatch across SPA routes)
    const cacheKey = `${LOCAL_CACHE_KEY_PREFIX}${apiResponse.fileName}`;
    const resolvedId = apiResponse.id > 0 ? apiResponse.id : createTemporaryLocalId();

    if (blob) {
      const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME);
      await cache.put(
        cacheKey,
        new Response(blob, {
          headers: { 'Content-Type': apiResponse.contentType || 'image/jpeg' },
        })
      );
    }

    const mediaRecord: DeviceImageRecord = {
      id: resolvedId,
      entityId: metadata.entityId,
      deviceImageTypeId: metadata.deviceImageTypeId,
      timestamp: metadata.timestamp,
      latitude: metadata.latitude,
      longitude: metadata.longitude,
      routeId: metadata.routeId ?? null,
      routeStopId: metadata.routeStopId ?? null,
      shipmentGuid: metadata.shipmentGuid ?? null,
      cacheKey: cacheKey,
      syncStatus: 'synced', // Already synced since it came from API
      retryCount: 0,
    };

    // Save metadata to Dexie using API/database id as canonical id
    await uow.runRW([db.deviceImages], async () => {
      await db.deviceImages.put(mediaRecord);
    });

    return mediaRecord;
  } catch (error) {
    console.error('Failed to save successful upload to cache:', error);
    throw new Error('Failed to save successful upload to cache.');
  }
};

export const upsertDeviceImageReferences = async (
  apiDeviceImages: DeviceImageReferenceDto[]
): Promise<DeviceImageRecord[]> => {
  try {
    const cachedRecords = await db.deviceImages.toArray();
    const cachedIndex = indexCachedRecords(cachedRecords);
    const rowsToUpsert: DeviceImageRecord[] = apiDeviceImages
      .map((dto) => mapReferenceForUpsert(dto, cachedIndex))
      .filter((row): row is DeviceImageRecord => row !== null);

    if (rowsToUpsert.length > 0) {
      await uow.runRW([db.deviceImages], async () => {
        await db.deviceImages.bulkPut(rowsToUpsert);
      });
    }

    return db.deviceImages.toArray();
  } catch (error) {
    console.error('Failed to upsert device image references:', error);
    throw error;
  }
};
